import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import UserContext from '../../contexts/UserContext';
import authenticate from '../../utils/Authenticate';
import displayError from '../../utils/ErrorHandler';
import Toast from './Toast';

// SuperAdmin-only screen to bulk-edit the Silat item catalog (name /
// requiredQuantity / unit / price) in one pass, instead of one-off manual
// MongoDB edits. Rows are editable in place; only changed rows are sent to
// the backend on Save.
const ManageSilatItems = () => {
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();

  const [items, setItems] = useState([]); // original, as fetched
  const [edits, setEdits] = useState({}); // itemId -> { name, unit, requiredQuantity, price }
  const [fieldErrors, setFieldErrors] = useState({}); // itemId -> { field: message }
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [toast, setToast] = useState({ message: '', type: 'success' });

  useEffect(() => {
    async function init() {
      if (!user) {
        const u = await authenticate(setUser, navigate);
        if (!u) return;
        if (u.role !== 'SuperAdmin') { navigate('/'); return; }
      } else if (user.role !== 'SuperAdmin') {
        navigate('/');
        return;
      }
      fetchItems();
    }
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URI}/api/silatItem/getAll`, { withCredentials: true });
      const data = res.data?.data || [];
      const sorted = [...data].sort((a, b) => a.name.localeCompare(b.name));
      setItems(sorted);
      setEdits({});
      setFieldErrors({});
    } catch (err) {
      const msg = displayError(err, navigate, setUser);
      if (msg) setMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  const getRowValue = (item, field) => {
    const edit = edits[item._id];
    if (edit && field in edit) return edit[field];
    return item[field];
  };

  const validateField = (field, value) => {
    if (field === 'name') return !String(value ?? '').trim() ? 'Required' : '';
    if (field === 'unit') return !String(value ?? '').trim() ? 'Required' : '';
    if (field === 'requiredQuantity' || field === 'price') {
      return value === '' || value == null || isNaN(value) || Number(value) < 0 ? 'Invalid' : '';
    }
    return '';
  };

  const handleCellChange = (item, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [item._id]: { ...prev[item._id], [field]: value },
    }));
    setFieldErrors((prev) => ({
      ...prev,
      [item._id]: { ...prev[item._id], [field]: validateField(field, value) },
    }));
  };

  const revertRow = (itemId) => {
    setEdits((prev) => {
      const copy = { ...prev };
      delete copy[itemId];
      return copy;
    });
    setFieldErrors((prev) => {
      const copy = { ...prev };
      delete copy[itemId];
      return copy;
    });
  };

  const dirtyIds = Object.keys(edits).filter((id) => Object.keys(edits[id] || {}).length > 0);
  const hasErrors = Object.values(fieldErrors).some((rowErrs) =>
    Object.values(rowErrs || {}).some(Boolean)
  );

  const filteredItems = useMemo(
    () => items.filter((it) => it.name.toLowerCase().includes(search.toLowerCase())),
    [items, search]
  );

  const handleSaveAll = async () => {
    if (dirtyIds.length === 0) return;
    if (hasErrors) {
      setMessage('Fix the highlighted fields before saving.');
      return;
    }

    const payload = dirtyIds.map((id) => {
      const item = items.find((it) => it._id === id);
      return {
        _id: id,
        name: getRowValue(item, 'name'),
        unit: getRowValue(item, 'unit'),
        requiredQuantity: Number(getRowValue(item, 'requiredQuantity')),
        price: Number(getRowValue(item, 'price')),
      };
    });

    setSaving(true);
    setMessage('');
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URI}/api/silatItem/bulkUpdate`,
        { items: payload },
        { withCredentials: true }
      );
      setToast({ message: `${payload.length} item${payload.length > 1 ? 's' : ''} updated successfully!`, type: 'success' });
      await fetchItems();
    } catch (err) {
      const msg = displayError(err, navigate, setUser);
      setToast({ message: msg || 'Failed to save changes.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const inputClass = (item, field) => {
    const err = fieldErrors[item._id]?.[field];
    const changed = edits[item._id] && field in edits[item._id];
    return `w-full p-1.5 border rounded text-sm ${
      err ? 'border-red-500 bg-red-50' : changed ? 'border-amber-400 bg-amber-50' : 'border-gray-300'
    }`;
  };

  return (
    <div className="p-4 pb-28">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-fmb">Manage Silat Items</h1>
          <p className="text-sm text-gray-600">
            Edit name, unit, required quantity, and price directly — changes apply to every Umaal's commit screen and dashboard.
          </p>
        </div>
        <input
          type="text"
          placeholder="Search items..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="p-2 border border-gray-300 rounded-lg w-full md:w-64"
        />
      </div>

      {message && (
        <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded p-2 mb-3">{message}</div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-[50vh]">
          <p>Loading items...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <p className="text-gray-600">No Silat items found.</p>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Required Qty</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price (₹)</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Row</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredItems.map((item) => {
                const isDirty = !!(edits[item._id] && Object.keys(edits[item._id]).length > 0);
                return (
                  <tr key={item._id} className={isDirty ? 'bg-amber-50/40' : ''}>
                    <td className="px-4 py-2 min-w-[180px]">
                      <input
                        type="text"
                        value={getRowValue(item, 'name') ?? ''}
                        onChange={(e) => handleCellChange(item, 'name', e.target.value)}
                        className={inputClass(item, 'name')}
                      />
                    </td>
                    <td className="px-4 py-2 min-w-[100px]">
                      <input
                        type="text"
                        value={getRowValue(item, 'unit') ?? ''}
                        onChange={(e) => handleCellChange(item, 'unit', e.target.value)}
                        className={inputClass(item, 'unit')}
                      />
                    </td>
                    <td className="px-4 py-2 min-w-[120px]">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={getRowValue(item, 'requiredQuantity') ?? ''}
                        onChange={(e) => handleCellChange(item, 'requiredQuantity', e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        className={inputClass(item, 'requiredQuantity')}
                      />
                    </td>
                    <td className="px-4 py-2 min-w-[120px]">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={getRowValue(item, 'price') ?? ''}
                        onChange={(e) => handleCellChange(item, 'price', e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        className={inputClass(item, 'price')}
                      />
                    </td>
                    <td className="px-4 py-2">
                      {isDirty && (
                        <button
                          onClick={() => revertRow(item._id)}
                          className="text-xs text-gray-500 hover:text-red-500 underline"
                        >
                          Revert
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {dirtyIds.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-yellow-100 shadow-lg flex justify-between items-center z-40">
          <span className="text-sm font-semibold">
            {dirtyIds.length} item{dirtyIds.length > 1 ? 's' : ''} changed
            {hasErrors && <span className="text-red-600 ml-2">Fix errors before saving</span>}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => { setEdits({}); setFieldErrors({}); }}
              className="px-4 py-2 rounded text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300"
            >
              Discard All
            </button>
            <button
              onClick={handleSaveAll}
              disabled={saving || hasErrors}
              className={`px-4 py-2 rounded text-white text-sm font-medium ${saving || hasErrors ? 'bg-gray-400' : 'bg-fmb hover:bg-fmb-dark'}`}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: '', type: 'success' })}
        />
      )}
    </div>
  );
};

export default ManageSilatItems;
